/**
 * KYC birthdate field. Display is day/month/year. The calendar opens with
 * Day, then Month, then Year. The saved value stays YYYY-MM-DD.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BIRTHDATE_MONTHS,
  BIRTHDATE_WEEKDAYS,
  birthdateAgeYears,
  buildBirthdateMonthGrid,
  clampBirthdateDay,
  dayMonthYearToIsoDate,
  daysInMonth,
  formatTypedBirthdate,
  isoDateToDayMonthYear,
  splitIsoDate,
} from '../../lib/kycDateDisplay.js';

interface KycBirthdateFieldProps {
  text: string;
  error: string | null;
  onTextChange: (text: string) => void;
  onCommit: (iso: string) => void;
  onInvalid: (message?: string) => void;
  showLabel?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  minAge?: number;
  minAgeMessage?: string;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_START = 1900;

export const KycBirthdateField: React.FC<KycBirthdateFieldProps> = ({
  text,
  error,
  onTextChange,
  onCommit,
  onInvalid,
  showLabel = true,
  label = 'Date of Birth',
  labelClassName = 'text-xs text-gray-400',
  minAge,
  minAgeMessage,
}) => {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLSelectElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const textRef = useRef(text);
  textRef.current = text;

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [draftDay, setDraftDay] = useState<number | ''>('');
  const [dayText, setDayText] = useState('');
  const [yearText, setYearText] = useState('');
  const [viewMonth, setViewMonth] = useState(1);
  const [viewYear, setViewYear] = useState(CURRENT_YEAR - 30);
  const [yearChosen, setYearChosen] = useState(false);

  const commitTyped = () => {
    const trimmed = textRef.current.trim();
    if (!trimmed) {
      onCommit('');
      return;
    }
    const iso = dayMonthYearToIsoDate(trimmed);
    if (!iso) {
      onInvalid();
      return;
    }
    onCommit(iso);
  };

  const placePanel = () => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 328;
    const height = 420;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = rect.bottom + 6;
    const top = below + height > window.innerHeight - 8 ? Math.max(8, rect.top - height - 6) : below;
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    const parsed = splitIsoDate(textRef.current);
    if (parsed) {
      setDraftDay(parsed.day);
      setDayText(String(parsed.day).padStart(2, '0'));
      setYearText(String(parsed.year));
      setViewMonth(parsed.month);
      setViewYear(parsed.year);
      setYearChosen(true);
    } else {
      setDraftDay('');
      setDayText('');
      setYearText('');
      setViewMonth(1);
      setViewYear(CURRENT_YEAR - 30);
      setYearChosen(false);
    }
    placePanel();
    const timer = window.setTimeout(() => dayRef.current?.focus(), 0);
    const onPlace = () => placePanel();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
      commitTyped();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        commitTyped();
      }
    };
    window.addEventListener('resize', onPlace);
    window.addEventListener('scroll', onPlace, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onPlace);
      window.removeEventListener('scroll', onPlace, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const applyParts = (day: number, month: number, year: number) => {
    const iso = clampBirthdateDay(year, month, day);
    if (!iso) return;
    const parts = splitIsoDate(iso);
    if (!parts) return;
    if (minAge != null) {
      const age = birthdateAgeYears(iso);
      if (age == null || age < minAge) {
        setDraftDay(parts.day);
        setDayText(String(parts.day).padStart(2, '0'));
        setViewMonth(parts.month);
        setViewYear(parts.year);
        setYearText(String(parts.year));
        setYearChosen(true);
        onInvalid(age != null && age < 0
          ? 'Date of birth cannot be in the future'
          : (minAgeMessage || `You must be at least ${minAge} years old`));
        return;
      }
    }
    setDraftDay(parts.day);
    setDayText(String(parts.day).padStart(2, '0'));
    setViewMonth(parts.month);
    setViewYear(parts.year);
    setYearText(String(parts.year));
    setYearChosen(true);
    onTextChange(isoDateToDayMonthYear(iso));
    onCommit(iso);
  };

  const dayCount = daysInMonth(viewYear, viewMonth) || 31;
  const grid = buildBirthdateMonthGrid(viewYear, viewMonth);
  const selectedIso = splitIsoDate(text)?.iso || '';

  const selectClass = 'w-full bg-gray-800 text-white text-sm px-2 py-1.5 rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none';

  const panel = open ? (
    <div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-label="Choose date of birth. Day, then month, then year."
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 328, zIndex: 80 }}
      className="rounded-xl border border-gray-600 bg-gray-900 shadow-2xl p-3"
    >
      <div className="grid grid-cols-3 gap-2">
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Day</span>
          <input
            ref={dayRef}
            type="text"
            inputMode="numeric"
            autoComplete="bday-day"
            placeholder="DD"
            maxLength={2}
            value={dayText}
            onChange={(event) => {
              const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
              setDayText(digits);
              if (digits.length < 2) return;
              const day = Number(digits);
              if (day < 1 || day > dayCount) return;
              setDraftDay(day);
              if (yearChosen && yearText.length === 4) applyParts(day, viewMonth, Number(yearText));
            }}
            onBlur={() => {
              if (!dayText) {
                setDraftDay('');
                return;
              }
              const day = Number(dayText);
              if (day < 1 || day > dayCount) return;
              setDraftDay(day);
              setDayText(String(day).padStart(2, '0'));
              if (yearChosen && yearText.length === 4) applyParts(day, viewMonth, Number(yearText));
            }}
            className={selectClass}
          />
        </label>
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Month</span>
          <select
            ref={monthRef}
            value={viewMonth}
            onChange={(event) => {
              const month = Number(event.target.value);
              const max = daysInMonth(viewYear, month) || 31;
              const typed = dayText ? Number(dayText) : 0;
              const day = typed >= 1 ? Math.min(typed, max) : 0;
              if (day) {
                setDraftDay(day);
                setDayText(String(day).padStart(2, '0'));
              }
              setViewMonth(month);
              if (day && yearChosen && yearText.length === 4) applyParts(day, month, Number(yearText));
            }}
            className={selectClass}
            style={{ colorScheme: 'dark' }}
          >
            {BIRTHDATE_MONTHS.map((name, index) => (
              <option key={name} value={index + 1}>{name}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Year</span>
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            autoComplete="bday-year"
            placeholder="YYYY"
            maxLength={4}
            value={yearText}
            onChange={(event) => {
              const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
              setYearText(digits);
              if (digits.length < 4) return;
              const year = Number(digits);
              if (year < YEAR_START || year > CURRENT_YEAR) {
                onInvalid('Enter a year from 1900 to the current year');
                return;
              }
              setViewYear(year);
              setYearChosen(true);
              const day = Number(dayText);
              if (day >= 1) applyParts(day, viewMonth, year);
            }}
            className={selectClass}
          />
        </label>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => {
            const month = viewMonth === 1 ? 12 : viewMonth - 1;
            const year = viewMonth === 1 ? viewYear - 1 : viewYear;
            if (year < YEAR_START) return;
            setViewMonth(month);
            setViewYear(year);
          }}
          className="w-8 h-8 rounded-md text-gray-300 hover:bg-gray-800 hover:text-white"
        >
          <svg viewBox="0 0 20 20" className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M12.5 4.5 L7 10 l5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="text-sm font-medium text-white">{BIRTHDATE_MONTHS[viewMonth - 1]} {viewYear}</div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => {
            const month = viewMonth === 12 ? 1 : viewMonth + 1;
            const year = viewMonth === 12 ? viewYear + 1 : viewYear;
            if (year > Math.max(CURRENT_YEAR, viewYear)) return;
            setViewMonth(month);
            setViewYear(year);
          }}
          className="w-8 h-8 rounded-md text-gray-300 hover:bg-gray-800 hover:text-white"
        >
          <svg viewBox="0 0 20 20" className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M7.5 4.5 L13 10 l-5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1 text-center">
        {BIRTHDATE_WEEKDAYS.map((name) => (
          <div key={name} className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 py-1">{name}</div>
        ))}
        {grid.map((cell, index) => {
          const typedDay = dayText ? Number(dayText) : 0;
          const selected = typedDay >= 1
            ? Boolean(cell.inMonth && cell.day === typedDay)
            : Boolean(cell.iso && cell.iso === selectedIso);
          return (
            <button
              key={`${cell.iso || 'x'}-${index}`}
              type="button"
              disabled={!cell.iso}
              onClick={() => {
                if (!cell.iso) return;
                const parts = splitIsoDate(cell.iso);
                if (!parts) return;
                setYearChosen(true);
                applyParts(parts.day, parts.month, parts.year);
              }}
              className={`h-8 rounded-md text-sm ${
                selected
                  ? 'bg-blue-600 text-white font-semibold'
                  : cell.inMonth
                    ? 'text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:bg-gray-800 hover:text-gray-300'
              } disabled:cursor-default`}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-700 flex items-center justify-between gap-2">
        <span className="text-sm text-white tabular-nums">
          {selectedIso
            ? isoDateToDayMonthYear(selectedIso)
            : draftDay
              ? `${String(draftDay).padStart(2, '0')} ${BIRTHDATE_MONTHS[viewMonth - 1]}`
              : 'Choose the day'}
        </span>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            commitTyped();
          }}
          className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
        >
          Done
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-1" ref={rootRef}>
      {showLabel && (
        <label className={labelClassName} htmlFor={`${panelId}-input`}>{label}</label>
      )}
      <div className={`flex rounded border bg-gray-700 focus-within:border-blue-500 ${error ? 'border-red-500' : 'border-gray-600'}`}>
        <input
          id={`${panelId}-input`}
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          placeholder="DD/MM/YYYY"
          maxLength={10}
          value={text}
          onChange={(event) => onTextChange(formatTypedBirthdate(event.target.value))}
          onBlur={(event) => {
            const next = event.relatedTarget as Node | null;
            if (panelRef.current?.contains(next) || rootRef.current?.contains(next)) return;
            if (!open) commitTyped();
          }}
          aria-invalid={error ? true : undefined}
          aria-expanded={open}
          aria-controls={panelId}
          className="w-full bg-transparent text-white text-sm px-3 py-1.5 rounded-l focus:outline-none"
        />
        <button
          type="button"
          aria-label="Open calendar"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            if (open) commitTyped();
            setOpen(!open);
          }}
          className="px-2.5 text-gray-300 hover:text-white border-l border-gray-600"
        >
          <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <rect x="3" y="4.5" width="14" height="12" rx="1.5" />
            <path d="M3 8 h14" />
            <path d="M7 3 v3 M13 3 v3" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      {panel && createPortal(panel, document.body)}
    </div>
  );
};
